import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useHistory } from 'react-router';
import { Path } from 'routes';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { TCompressionBooking } from 'types/compression';
import { DATE_FORMAT } from 'util/ConstantValues';
import { TEMPORARY_STORAGE_COLORS_PALETTE } from 'util/ConstantValues';
import {
  getBrightnessFromColor,
  mergeClasses,
  normalizeNumber,
} from 'util/commons';
import { CompressionDeliveryContent } from '../CompressionDeliveryContent';
import Popover from 'components/atoms/Popover';
import styles from './CompressionBookingItem.module.scss';

type CompressionBookingItemProps = {
  compression: TCompressionBooking;
  key: number;
  isStorage?: boolean;
};

const CompressionBookingItem = ({
  compression,
  key,
  isStorage,
}: CompressionBookingItemProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const onClickQRCode = () => {
    history.push(
      `${Path.shippingRegistrationDetail}?deliveryId=${compression.deliveryId}`,
      compression
    );
  };

  return (
    <div key={key} className={styles.CompressionBookingItem}>
      <div className={mergeClasses(styles.w65, styles.content)}>
        <div className={styles.contentRow}>
          <div className={styles.left}>{t('compressions.delivery_date')}</div>
          <div className={styles.right}>
            <CompressionDeliveryContent
              contents={getFormatDateWithCurrentTimezone(
                compression.requestedDatetime,
                DATE_FORMAT.slaYMDHm
              )}
            />
          </div>
        </div>
        <div className={styles.contentRow}>
          <div className={styles.left}>
            {t('compressions.delivery_destination')}
          </div>
          <div className={styles.right}>
            <CompressionDeliveryContent
              contents={compression.temporaryTargetSite.name}
            />
          </div>
        </div>
        <div className={styles.contentRow}>
          <div className={styles.left}>{t('compressions.material_name')}</div>
          <div className={styles.right}>
            <CompressionDeliveryContent contents={compression.description} />
          </div>
        </div>
        <div className={styles.contentRow}>
          {isStorage ? (
            <>
              <div className={styles.left}>
                {t(
                  'compressions.shipment.shipment_registration.columns.local_storage'
                )}
              </div>
              <div className={styles.right}>
                {(() => {
                  const locs = compression.storageLocations;
                  if (!locs?.length)
                    return (
                      <CompressionDeliveryContent
                        contents={t('storage_location_registration.not_set')}
                      />
                    );

                  return (
                    <CompressionDeliveryContent
                      contents={
                        <div className={styles.storageLocationList}>
                          {locs.map((loc) => {
                            const bgColor =
                              TEMPORARY_STORAGE_COLORS_PALETTE[
                                Number(loc.color) %
                                  TEMPORARY_STORAGE_COLORS_PALETTE.length
                              ];
                            return (
                              <span
                                key={loc.name}
                                className={styles.storageLocationBadge}
                                style={{
                                  backgroundColor: bgColor,
                                  color:
                                    getBrightnessFromColor(bgColor) <= 100
                                      ? '#ffffff'
                                      : '#1b1b1b',
                                }}
                              >
                                <Popover
                                  autoGen={{
                                    lines: 1,
                                    odd: 4,
                                    wrapperStyle: {
                                      width: 'fit-content',
                                    },
                                  }}
                                  popUpStyle={{
                                    fontSize: 18,
                                  }}
                                  positions={['top']}
                                  content={loc.name}
                                  containerStyle={{
                                    maxWidth: '400px',
                                    wordBreak: 'break-all',
                                    zIndex: '1000',
                                  }}
                                >
                                  <span>{loc.name}</span>
                                </Popover>
                              </span>
                            );
                          })}
                        </div>
                      }
                    />
                  );
                })()}
              </div>
            </>
          ) : (
            <>
              <div className={styles.left}>{t('compressions.grade')}</div>
              <div className={styles.right}>
                <CompressionDeliveryContent contents={compression.grade} />
              </div>
            </>
          )}
        </div>
        <div className={styles.contentRow}>
          <div className={styles.left}>{t('compressions.weight')}</div>
          <div className={styles.right}>
            <CompressionDeliveryContent
              contents={`${normalizeNumber({ value: compression.weight })}kg`}
            />
          </div>
        </div>
      </div>
      <div className={styles.w35}>
        <div className={styles.QRContainer}>
          <QRCode
            size={80}
            className={styles.QRCode}
            value={compression.qrCode}
            viewBox={`0 0 80 80`}
            onClick={onClickQRCode}
          />
        </div>
      </div>
    </div>
  );
};

export default CompressionBookingItem;
